import { useState, useEffect, useRef, useCallback } from 'react';
// We use type-only imports for type safety, and dynamic import for the implementation.
import type Peer from 'peerjs';
import type { DataConnection, MediaConnection } from 'peerjs';

export interface Reaction {
  emoji: string;
  by: string;
}

export interface Message {
  id: string;
  sender: 'me' | 'them' | 'system';
  content: string;
  timestamp: string;
  nickname?: string;
  reactions?: Reaction[];
  // New props for file sharing
  messageType?: 'text' | 'file' | 'system' | 'voice';
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  fileData?: string; // base64 encoded
  voiceData?: string; // base64 encoded audio
  voiceDuration?: number;
  isEdited?: boolean;
  isDeleted?: boolean;
}

export type DataType = {
  type: 'chat';
  payload: Omit<Message, 'sender'>;
} | {
  type: 'file_start';
  payload: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    totalChunks: number;
    timestamp: string;
    nickname?: string;
    isVoice?: boolean; // To distinguish voice messages
    duration?: number; // For voice messages
  };
} | {
  type: 'file_chunk';
  payload: {
    id: string;
    chunkIndex: number;
    chunk: string; // base64 encoded chunk
  };
} | {
  type: 'file_ack'; // Optional: to acknowledge chunks for flow control if needed
  payload: {
    id: string;
    chunkIndex: number;
  };
} | {
  type: 'file'; // Deprecated for large files, keeping for backward compat or small updates
  payload: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    fileData: string;
    timestamp: string;
    nickname?: string;
  };
} | {
  type: 'voice'; // Deprecated for large voice, keeping for backward compat
  payload: {
    id: string;
    voiceData: string;
    duration: number;
    timestamp: string;
    nickname?: string;
  };
} | {
  type: 'edit_message';
  payload: {
    id: string;
    newContent: string;
  };
} | {
  type: 'delete_message';
  payload: {
    id: string;
  };
} | {
  type: 'clear_chat';
  payload: null;
} | {
  type: 'video';
  payload: string;
} | {
  type: 'system';
  payload: string;
} | {
  type: 'nickname';
  payload: string;
} | {
  type: 'reaction';
  payload: {
    messageId: string;
    reaction: Reaction;
  };
} | {
  type: 'player_state';
  payload: {
    event: 'play' | 'pause';
    currentTime: number;
  };
} | {
  type: 'request_sync';
  payload: null;
} | {
  type: 'playlist_share';
  payload: {
    playlist: any;
    sharedBy: string;
    timestamp: string;
  };
} | {
  type: 'browser_sync';
  payload: {
    url: string;
    action: 'navigate' | 'scroll';
    scrollPosition?: { x: number; y: number };
  };
} | {
  type: 'screen_share_start';
  payload: {
    nickname: string;
  };
} | {
  type: 'screen_share_stop';
  payload: {
    nickname: string;
  };
};

// Generate a fallback peer ID if PeerJS server fails
const generateFallbackPeerId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 8);
  return `togetherly${timestamp}${randomPart}`;
};

export const usePeer = (initialPeerId?: string | null) => {
  const [peer, setPeer] = useState<Peer | null>(null);
  const [peerId, setPeerId] = useState('');
  const [conn, setConn] = useState<DataConnection | null>(null);
  const [data, setData] = useState<DataType | null>(null);
  const peerInstance = useRef<Peer | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected' | 'failed' | 'reconnecting'>('disconnected');
  const isReconnecting = useRef(false);
  const reconnectAttempts = useRef(0);

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const mediaCallInstance = useRef<MediaConnection | null>(null);

  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [remoteScreenStream, setRemoteScreenStream] = useState<MediaStream | null>(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenCallInstance = useRef<MediaConnection | null>(null);

  const [incomingConn, setIncomingConn] = useState<DataConnection | null>(null);

  const setupConnectionHandlers = useCallback((connection: DataConnection) => {
    console.log('📡 Setting up handlers for connection:', connection.peer);
    console.log('📡 Connection open?', connection.open);
    console.log('📡 Connection type:', connection.type);

    // Setup timeout for connection establishment
    const connectionTimeout = setTimeout(() => {
      if (!connection.open) {
        console.log('⏱️ Connection attempt timed out after 30 seconds');
        connection.close();
        setConnectionState('failed');
        setData({ type: 'system', payload: 'Connection timed out. Please check the Peer ID and try again.' });
      }
    }, 30000); // 30 seconds timeout

    const onOpen = () => {
      clearTimeout(connectionTimeout);
      console.log('✅ Connection opened with:', connection.peer);
      setIsConnected(true);
      setConnectionState('connected');
      setData({ type: 'system', payload: `Connected to ${connection.peer}` });
    };

    const onData = (receivedData: unknown) => {
      console.log('📥 Received data:', receivedData);
      setData(receivedData as DataType);
    };

    const onClose = () => {
      clearTimeout(connectionTimeout);
      console.log('🔌 Connection closed with:', connection.peer);
      setIsConnected(false);
      setConnectionState('disconnected');
      setConn(null);
      setData({ type: 'system', payload: 'Peer has disconnected.' });
    };

    const onError = (err: Error) => {
      clearTimeout(connectionTimeout);
      console.error('❌ Connection error:', err);
      setConnectionState('failed');

      let errorMessage = `Connection error: ${err.message}`;
      if (err.message.includes('IceConnectionState of FAILED')) {
        errorMessage = 'Could not connect to peer. This might be due to a firewall or network restriction. Try switching networks (e.g., Wi-Fi to 4G).';
      }

      setData({ type: 'system', payload: errorMessage });
    };

    connection.on('open', onOpen);
    connection.on('data', onData);
    connection.on('close', onClose);
    connection.on('error', onError);

    // This handles the case where the connection is already open
    if (connection.open) {
      console.log('🚀 Connection already open, calling onOpen immediately');
      onOpen();
    }

    return () => {
      clearTimeout(connectionTimeout);
      connection.off('open', onOpen);
      connection.off('data', onData);
      connection.off('close', onClose);
      connection.off('error', onError);
    };
  }, []);

  const endCall = useCallback(() => {
    if (mediaCallInstance.current) {
      mediaCallInstance.current.close();
    }
    setLocalStream(prevStream => {
      if (prevStream) {
        prevStream.getTracks().forEach(track => track.stop());
      }
      return null;
    });
    setRemoteStream(null);
    setIsCallActive(false);
    mediaCallInstance.current = null;
    setData({ type: 'system', payload: 'Call ended.' });
  }, []);

  const disconnectPeer = useCallback(() => {
    if (conn) {
      console.log('Manual disconnection requested');
      conn.close();
      setConn(null);
      setIsConnected(false);
      setConnectionState('disconnected');
      setData({ type: 'system', payload: 'Disconnected from peer.' });
    }
    endCall(); // Ensure calls are also ended
  }, [conn, endCall]);

  const acceptConnection = useCallback(() => {
    if (incomingConn) {
      console.log('Accepting connection from:', incomingConn.peer);
      setConn(incomingConn);
      setIncomingConn(null);

      // Set up handlers immediately after accepting
      setTimeout(() => {
        if (incomingConn.open) {
          setIsConnected(true);
          setConnectionState('connected');
          setData({ type: 'system', payload: `Connected to ${incomingConn.peer}` });
        }
      }, 100);
    }
  }, [incomingConn]);

  const rejectConnection = useCallback(() => {
    if (incomingConn) {
      console.log('Rejecting connection from:', incomingConn.peer);
      incomingConn.close();
      setIncomingConn(null);
      setData({ type: 'system', payload: 'Connection request rejected.' });
    }
  }, [incomingConn]);

  // Manual reconnect function
  const onManualReconnect = useCallback(() => {
    if (isReconnecting.current) return;

    if (peerInstance.current && !peerInstance.current.destroyed) {
      console.log('Manual reconnect triggered');
      isReconnecting.current = true;
      setConnectionState('reconnecting');
      setData({ type: 'system', payload: 'Reconnecting...' });

      peerInstance.current.reconnect();

      // Reset flag after a delay
      setTimeout(() => {
        isReconnecting.current = false;
      }, 5000);
    } else {
      // Reinitialize peer if it's destroyed
      window.location.reload();
    }
  }, []);

  // Handle page visibility changes for aggressive reconnection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Page became visible');

        // Check connection health
        if (conn && !conn.open) {
          console.log('👁️ Connection found closed on visibility change, reconnecting...');
          onManualReconnect();
        } else if (peerInstance.current && peerInstance.current.disconnected) {
          console.log('👁️ Peer found disconnected on visibility change, reconnecting...');
          peerInstance.current.reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [conn, onManualReconnect]);

  useEffect(() => {
    const initializePeer = async () => {
      try {
        // Don't reuse stored peer IDs - they cause conflicts when PeerJS server
        // already has a session. Generate fresh IDs each time for reliability.

        // Dynamically import PeerJS to solve module resolution issues with Vite
        const { default: Peer } = await import('peerjs');

        // Use a single reliable PeerJS server so all users connect to the same one
        const serverConfigs = [
          {
            host: '0.peerjs.com',
            port: 443,
            path: '/',
            secure: true
          }
        ];

        let peerInitialized = false;
        let attempts = 0;

        const tryInitializePeer = async (configIndex = 0) => {
          if (configIndex >= serverConfigs.length) {
            // All servers failed, use fallback ID
            const fallbackId = generateFallbackPeerId();
            console.log('All PeerJS servers failed, using fallback ID:', fallbackId);
            setPeerId(fallbackId);
            setData({ type: 'system', payload: 'Using offline mode. Share your ID to connect when both users are online.' });
            return;
          }

          const config = serverConfigs[configIndex];
          console.log(`Initializing peer with config ${configIndex + 1}/${serverConfigs.length}:`, config.host);

          // Use permanent ID for registered users, undefined = PeerJS auto-generates
          const newPeer = new Peer(initialPeerId ?? undefined, {
            ...config,
            config: {
              iceServers: [
                // Google STUN servers - Highly reliable
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' },
                { urls: 'stun:stun3.l.google.com:19302' },
                { urls: 'stun:stun4.l.google.com:19302' },
                // Mozilla STUN server
                { urls: 'stun:stun.services.mozilla.com' },
                // Free TURN servers for NAT traversal (critical for mobile/restricted networks)
                {
                  urls: 'turn:openrelay.metered.ca:80',
                  username: 'openrelayproject',
                  credential: 'openrelayproject',
                },
                {
                  urls: 'turn:openrelay.metered.ca:443',
                  username: 'openrelayproject',
                  credential: 'openrelayproject',
                },
                {
                  urls: 'turn:openrelay.metered.ca:443?transport=tcp',
                  username: 'openrelayproject',
                  credential: 'openrelayproject',
                },
              ],
              sdpSemantics: 'unified-plan',
              iceCandidatePoolSize: 10,
              bundlePolicy: 'max-bundle',
              rtcpMuxPolicy: 'require',
              iceTransportPolicy: 'all'
            },
            debug: 1,
            pingInterval: 5000,
            secure: true
          });

          // Set timeout for peer initialization
          const initTimeout = setTimeout(() => {
            if (!peerInitialized) {
              console.log(`Peer initialization timeout for server ${configIndex + 1}, trying next...`);
              newPeer.destroy();
              tryInitializePeer(configIndex + 1);
            }
          }, 10000); // 10 second timeout

          newPeer.on('open', (id: string) => {
            if (peerInitialized) return;
            peerInitialized = true;
            clearTimeout(initTimeout);

            console.log('Peer opened with ID:', id);
            setPeerId(id);
            peerInstance.current = newPeer;
            setPeer(newPeer);
            setData({ type: 'system', payload: `Peer ID generated: ${id}` });
          });

          newPeer.on('error', (error: any) => {
            clearTimeout(initTimeout);
            console.error(`Peer error on server ${configIndex + 1}:`, error);

            if (!peerInitialized) {
              console.log(`Server ${configIndex + 1} failed, trying next...`);
              newPeer.destroy();
              tryInitializePeer(configIndex + 1);
              return;
            }

            setData({ type: 'system', payload: `Peer error: ${error.message || 'Connection failed'}` });

            // Enhanced error recovery for connected peers
            if (error.type === 'network' || error.type === 'disconnected' || error.type === 'server-error') {
              console.log('Network error, retrying in 3000ms... (1/3)');
              setTimeout(() => {
                if (!newPeer.destroyed) {
                  console.log('Attempting to reconnect after error...');
                  newPeer.reconnect();
                }
              }, 3000);
            }
          });

          newPeer.on('disconnected', () => {
            if (!peerInitialized) return;

            console.log('Peer disconnected, attempting to reconnect...');
            setConnectionState('reconnecting');
            setData({ type: 'system', payload: 'Connection lost, reconnecting...' });

            // Attempt to reconnect with exponential backoff
            if (reconnectAttempts.current < 5) {
              reconnectAttempts.current++;
              const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);

              setTimeout(() => {
                if (!newPeer.destroyed) {
                  console.log(`Reconnection attempt ${reconnectAttempts.current}`);
                  newPeer.reconnect();
                }
              }, delay);
            } else {
              setConnectionState('failed');
              setData({ type: 'system', payload: 'Connection lost. Please refresh or try reconnecting manually.' });
            }
          });

          // ... keep existing code for connection and call handlers
          newPeer.on('connection', (newConn: DataConnection) => {
            console.log('📞 Incoming connection from:', newConn.peer);
            console.log('📞 Connection metadata:', newConn.metadata);

            if (conn?.open || isCallActive || incomingConn) {
              console.log('⛔ Rejecting connection - already busy');
              newConn.close();
              return;
            }

            const connectionTimeout = setTimeout(() => {
              if (!newConn.open) {
                console.log('⏱️ Connection request timed out after 5 minutes');
                newConn.close();
                setIncomingConn(null);
                setData({ type: 'system', payload: 'Connection request timed out after 5 minutes.' });
              }
            }, 300000);

            newConn.on('open', () => {
              clearTimeout(connectionTimeout);
              console.log('✅ Incoming connection established');
            });

            newConn.on('error', (err) => {
              console.error('❌ Incoming connection error:', err);
              clearTimeout(connectionTimeout);
              setIncomingConn(null);
              setData({ type: 'system', payload: `Connection error: ${err.message}` });
            });

            setData({
              type: 'system',
              payload: `Incoming connection from ${newConn.metadata?.nickname || newConn.peer}`
            });
            setIncomingConn(newConn);
          });

          const setupCallListeners = (call: MediaConnection) => {
            call.on('stream', (stream) => {
              setRemoteStream(stream);
            });
            call.on('close', () => {
              endCall();
            });
            call.on('error', (err) => {
              console.error('Call error:', err);
              setData({ type: 'system', payload: `Call error: ${err.message}` });
              endCall();
            });
            mediaCallInstance.current = call;
            setIsCallActive(true);
          };

          newPeer.on('call', (incomingCall: MediaConnection) => {
            const callType = incomingCall.metadata?.callType === 'audio' ? 'audio' : 'video';
            setData({ type: 'system', payload: `Incoming ${callType} call from ${incomingCall.peer}...` });

            const constraints = { video: callType === 'video', audio: true };

            if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
              console.warn('Media devices or getUserMedia not available in this environment');
              if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
                setData({ type: 'system', payload: 'Secure connection (HTTPS) is required for video/audio calls on mobile.' });
              } else {
                setData({ type: 'system', payload: 'Camera/Mic access is not supported on this browser.' });
              }
              return;
            }

            navigator.mediaDevices.getUserMedia(constraints)
              .then(stream => {
                setLocalStream(stream);
                incomingCall.answer(stream);
                setupCallListeners(incomingCall);
              })
              .catch(err => {
                console.error(`Failed to get local stream for incoming ${callType} call`, err);
                setData({ type: 'system', payload: 'Could not start camera/mic.' });
              });
          });
        };

        await tryInitializePeer();

      } catch (error) {
        console.error('Failed to initialize peer:', error);

        // Use fallback ID generation
        const fallbackId = generateFallbackPeerId();
        console.log('Using fallback peer ID:', fallbackId);
        setPeerId(fallbackId);
        setData({ type: 'system', payload: 'Using offline mode. Connection will work when both users are online.' });
      }
    };

    initializePeer();

    return () => {
      if (peerInstance.current) {
        peerInstance.current.destroy();
      }
    };
  }, [endCall]);

  useEffect(() => {
    if (!conn) return;

    console.log('Setting up handlers for connection:', conn.peer);
    const cleanup = setupConnectionHandlers(conn);

    return cleanup;
  }, [conn, setupConnectionHandlers]);

  const connectToPeer = useCallback((remoteId: string, metadata: { nickname: string }) => {
    if (!peer) {
      console.error('Peer not initialized');
      setData({ type: 'system', payload: 'Peer not ready. Please wait and try again.' });
      return;
    }

    if (!remoteId.trim()) {
      setData({ type: 'system', payload: 'Please enter a valid Peer ID' });
      return;
    }

    if (remoteId === peerId) {
      console.error('Cannot connect to self');
      setData({ type: 'system', payload: 'Cannot connect to your own Peer ID' });
      return;
    }

    console.log('🔄 Attempting to connect to peer:', remoteId);
    console.log('🔄 My peer ID:', peerId);
    console.log('🔄 Peer instance:', peer);
    console.log('🔄 Peer destroyed?', peer.destroyed);
    console.log('🔄 Peer disconnected?', peer.disconnected);

    setConnectionState('connecting');
    setData({ type: 'system', payload: `Connecting to ${remoteId}...` });

    try {
      const newConn = peer.connect(remoteId, {
        metadata,
        reliable: true,
        serialization: 'json'
      });

      console.log('✅ Connection object created:', newConn);
      console.log('✅ Connection peer:', newConn.peer);
      console.log('✅ Connection open?', newConn.open);

      // Set connection immediately so useEffect can set up handlers
      setConn(newConn);
    } catch (error) {
      console.error('❌ Error creating connection:', error);
      setConnectionState('failed');
      setData({ type: 'system', payload: 'Failed to create connection. Please try again.' });
    }
  }, [peer, peerId]);

  const sendData = useCallback((data: DataType) => {
    if (conn && conn.open) {
      // console.log('Sending data:', data); // Reduce log noise
      try {
        conn.send(data);
      } catch (error) {
        console.error('Failed to send data:', error);
        setData({ type: 'system', payload: 'Failed to send message. Connection may be unstable.' });
        // Try to reconnect if send fails
        onManualReconnect();
      }
    } else {
      console.log('Connection not ready, data will be queued');
      if (conn) {
        // Only queue if connection exists but maybe temporarily closed
        const openHandler = () => {
          try {
            conn.send(data);
            conn.off('open', openHandler);
          } catch (error) {
            console.error('Failed to send queued data:', error);
          }
        };
        // Add a timeout to the queue
        const queueTimeout = setTimeout(() => {
          conn.off('open', openHandler);
          console.log('Queued message timed out');
        }, 5000);

        conn.on('open', () => {
          clearTimeout(queueTimeout);
          openHandler();
        });
      }
    }
  }, [conn, onManualReconnect]);

  const startCall = useCallback((type: 'audio' | 'video') => {
    if (!peer || !conn) return;

    const setupCallListeners = (call: MediaConnection) => {
      call.on('stream', (stream) => {
        setRemoteStream(stream);
      });
      call.on('close', () => {
        endCall();
      });
      call.on('error', (err) => {
        console.error('Call error:', err);
        setData({ type: 'system', payload: `Call error: ${err.message}` });
        endCall();
      });
      mediaCallInstance.current = call;
      setIsCallActive(true);
    };

    const constraints = { video: type === 'video', audio: true };

    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('navigator.mediaDevices not available');
      setData({ type: 'system', payload: 'Camera/Mic not supported or blocked in this browser.' });
      return;
    }

    navigator.mediaDevices.getUserMedia(constraints)
      .then(stream => {
        setLocalStream(stream);
        const outgoingCall = peer.call(conn.peer, stream, { metadata: { callType: type } });
        setupCallListeners(outgoingCall);
      })
      .catch(err => {
        console.error('Failed to get local stream', err);
        setData({ type: 'system', payload: 'Could not start camera/mic. Please check permissions.' });
      });
  }, [peer, conn, endCall]);

  /**
   * startDirectCall — initiates a media call to a peer ID directly,
   * WITHOUT needing an existing data connection (conn).
   * Used for friend calls via Supabase signaling where peer IDs are
   * exchanged through the DB, not through a manual data channel.
   */
  const startDirectCall = useCallback((remotePeerId: string, type: 'audio' | 'video') => {
    if (!peer) {
      console.error('[startDirectCall] Peer not initialized');
      return;
    }

    const setupCallListeners = (call: import('peerjs').MediaConnection) => {
      call.on('stream', (stream) => { setRemoteStream(stream); });
      call.on('close', () => { endCall(); });
      call.on('error', (err) => {
        console.error('Direct call error:', err);
        endCall();
      });
      mediaCallInstance.current = call;
      setIsCallActive(true);
    };

    const constraints = { video: type === 'video', audio: true };

    navigator.mediaDevices.getUserMedia(constraints)
      .then(stream => {
        setLocalStream(stream);
        const outgoingCall = peer.call(remotePeerId, stream, {
          metadata: { callType: type }
        });
        setupCallListeners(outgoingCall);
      })
      .catch(err => {
        console.error('[startDirectCall] getUserMedia failed:', err);
        setData({ type: 'system', payload: 'Could not access camera/mic. Check permissions.' });
      });
  }, [peer, endCall]);

  const toggleMedia = useCallback((type: 'audio' | 'video') => {
    if (localStream) {
      const tracks = type === 'audio' ? localStream.getAudioTracks() : localStream.getVideoTracks();
      if (tracks[0]) {
        tracks[0].enabled = !tracks[0].enabled;
      }
    }
  }, [localStream]);

  const startScreenShare = useCallback(async () => {
    if (!peer || !conn) return;

    try {
      // High-quality screen sharing constraints
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 30, max: 60 },
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      } as DisplayMediaStreamOptions);

      setScreenStream(stream);
      setIsScreenSharing(true);

      // Notify peer that screen sharing has started
      sendData({ type: 'screen_share_start', payload: { nickname: conn.metadata?.nickname || 'Friend' } });

      // Setup screen share call with optimized settings
      const outgoingCall = peer.call(conn.peer, stream, {
        metadata: { callType: 'screen' },
        sdpTransform: (sdp: string) => {
          // Increase bandwidth for better quality
          return sdp.replace(/b=AS:(\d+)/g, 'b=AS:8000');
        }
      });

      outgoingCall.on('stream', (remoteScreenStream) => {
        console.log('Receiving remote screen stream');
        setRemoteScreenStream(remoteScreenStream);
      });

      outgoingCall.on('close', () => {
        console.log('Screen share call closed');
        stopScreenShare();
      });

      outgoingCall.on('error', (err) => {
        console.error('Screen share error:', err);
        setData({ type: 'system', payload: `Screen share error: ${err.message}` });
        stopScreenShare();
      });

      screenCallInstance.current = outgoingCall;

      // Stop screen share when user stops sharing from browser UI
      stream.getTracks().forEach(track => {
        track.onended = () => {
          console.log('Screen share track ended');
          stopScreenShare();
        };
      });

    } catch (err: any) {
      console.error('Failed to start screen share:', err);
      let message = 'Could not start screen sharing.';
      if (err.name === 'NotAllowedError') {
        message = 'Screen sharing permission denied.';
      } else if (err.name === 'NotFoundError') {
        message = 'No screen available to share.';
      } else if (err.name === 'NotReadableError') {
        message = 'Screen sharing already in use.';
      }
      setData({ type: 'system', payload: message });
      throw err;
    }
  }, [peer, conn, sendData]);

  const stopScreenShare = useCallback(() => {
    if (screenCallInstance.current) {
      screenCallInstance.current.close();
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
    }
    setScreenStream(null);
    setRemoteScreenStream(null);
    setIsScreenSharing(false);
    screenCallInstance.current = null;

    // Notify peer that screen sharing has stopped
    if (conn) {
      sendData({ type: 'screen_share_stop', payload: { nickname: conn.metadata?.nickname || 'Friend' } });
    }
  }, [screenStream, conn, sendData]);

  return {
    peerId,
    peer,
    connectToPeer,
    sendData,
    data,
    isConnected,
    conn,
    localStream,
    remoteStream,
    isCallActive,
    startCall,
    startDirectCall,
    endCall,
    toggleMedia,
    screenStream,
    remoteScreenStream,
    isScreenSharing,
    startScreenShare,
    stopScreenShare,
    disconnectPeer,
    incomingConn,
    acceptConnection,
    rejectConnection,
    connectionState,
    onManualReconnect
  };
};
